import store from "@/store";
import { io, Socket } from "socket.io-client";
import baseAPI from "@/api/base";
import { socketConnected, rejoinAll, clearTrackedRooms } from "@/utils/socket-rooms";

let socket: Socket | null = null;

/**
 * Decode JWT payload without verification (for client-side expiration check)
 */
function decodeJWT(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Check if token will expire within the specified hours
 */
function willTokenExpireSoon(token: string, hoursThreshold = 24): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return false;

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const thresholdTime = Date.now() + hoursThreshold * 60 * 60 * 1000;

  return expirationTime < thresholdTime;
}

/**
 * Refresh authentication token
 */
async function refreshAuthToken(): Promise<string | null> {
  try {
    const response = await baseAPI.post("/auth/refresh-token");

    if (response.data?.sessionToken) {
      const newToken = response.data.sessionToken;
      store.commit("SET_SESSION_TOKEN", newToken);
      return newToken;
    }

    console.warn("⚠️ Token refresh response missing sessionToken");
    return null;
  } catch (error) {
    console.error("❌ Failed to refresh token:", error);
    return null;
  }
}

/**
 * Connection state type
 */
export type SocketConnectionState = "connecting" | "connected" | "disconnected" | "error";

/**
 * Get the current socket connection state
 */
export function getSocketState(): SocketConnectionState {
  if (!socket) return "disconnected";
  if (socket.connected) return "connected";
  if (socket.disconnected) return "disconnected";
  return "connecting";
}

/**
 * Check if socket is connected
 */
export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Disconnect and cleanup socket
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  // socketConnected left as-is; logout flow navigates away immediately
  clearTrackedRooms();
}

/**
 * Get or create Socket.IO connection
 * Uses websocket as primary transport with polling fallback
 */
export function getSocket(): Socket {
  if (typeof io !== "function") {
    console.error("Socket.IO client library not loaded");
    throw new Error("Socket.IO client library not loaded");
  }

  // Return existing socket (connected or reconnecting)
  if (socket) {
    return socket;
  }

  // Get socket URL based on environment
  const isProduction = process.env.NODE_ENV === "production";
  const socketURL = isProduction
    ? process.env.VUE_APP_SOCKET_URL || window.location.origin
    : process.env.VUE_APP_LOCAL_SOCKET_URL || "http://localhost:8080";

  // Get authentication token
  const token = store.getters.sessionToken;

  // Create socket connection
  // Enable websocket upgrade in all environments for better performance
  socket = io(socketURL, {
    transports: ["polling", "websocket"],
    upgrade: true,
    withCredentials: true,
    auth: { token },
    query: token ? { token } : undefined,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  });

  socket.on("connect", async () => {
    socketConnected.value = true;
    socket?.emit("ping", { timestamp: Date.now() });

    const currentToken = store.getters.sessionToken;
    if (currentToken && willTokenExpireSoon(currentToken, 24)) {
      const newToken = await refreshAuthToken();
      if (newToken && socket) {
        socket.auth = { token: newToken };
      }
    }
  });

  socket.on("connect_error", (error: any) => {
    console.error("❌ Socket.IO connection error", socketURL, socket?.id, error);
  });

  socket.on("disconnect", (reason) => {
    socketConnected.value = false;
    console.warn("🔌 Socket.IO disconnected", socketURL, socket?.id, reason);
  });

  socket.on("error", (error) => {
    console.error("❌ Socket.IO error:", socketURL, socket?.id, error);
  });

  socket.io.on("reconnect_attempt", (_attempt) => {});

  socket.io.on("reconnect", (_attempt) => {
    socketConnected.value = true;
    if (socket) rejoinAll(socket);
  });

  socket.io.on("reconnect_failed", () => {
    console.error("❌ Socket.IO: All reconnection attempts failed", socketURL, socket?.id);
  });

  socket.io.on("error", (error) => {
    console.error("❌ Socket.IO engine error:", error);
  });

  // Connect if not already connected
  if (!socket.connected) {
    socket.connect();
  }

  return socket;
}
